import { createBrowserRouter, LazyRouteFunction, RouteObject } from 'react-router-dom';

import AuthLayout from '~/layouts/auth-layout';
import MainLayout from '~/layouts/main-layout';
import { StaffInvitationLayout } from '~/layouts/StaffInvitation.layout';
import VerifyLayout from '~/layouts/verify-layout';
import routes from './routes';
import { RouterFallbackElement } from '~/modules/@shared/components/ErrorBoundary/components/RouterFallbackElement';
import { KioskLayout } from '~/modules/kiosk/components/KioskLayout';
import LoadingElement from '~/modules/@shared/components/LoadingElement/LoadingElement.tsx';
import { ComponentType } from 'react';

const router = createBrowserRouter([
  {
    path: '/',
    Component: MainLayout as unknown as ComponentType<{}>,
    HydrateFallback: LoadingElement as unknown as ComponentType<{}>,
    errorElement: <RouterFallbackElement />,
    children: [
      {
        path: routes.root,
        lazy: (() => import('../pages/home')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.members.root,
        children: [
          {
            path: routes.members.members.root,
            children: [
              {
                path: routes.members.members.list,
                lazy: (() => import('../pages/members/members/list')) as unknown as LazyRouteFunction<RouteObject>,
              },
              {
                path: routes.members.members.create,
                lazy: (() => import('../pages/members/members/create')) as unknown as LazyRouteFunction<RouteObject>,
              },
              {
                path: routes.members.members.member.root,
                children: [
                  {
                    path: routes.members.members.member.detail,
                    lazy: (() =>
                      import(
                        '../pages/members/members/member/detail.tsx'
                      )) as unknown as LazyRouteFunction<RouteObject>,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: routes.operations.root,
        children: [
          {
            path: routes.operations.plans.root,
            children: [
              {
                path: routes.operations.plans.list,
                lazy: (() => import('../pages/operations/plans/list')) as unknown as LazyRouteFunction<RouteObject>,
              },
              {
                path: routes.operations.plans.create,
                lazy: (() => import('../pages/operations/plans/create')) as unknown as LazyRouteFunction<RouteObject>,
                // lazy: () => import('../pages/members/members/create'),
              },
              {
                path: routes.operations.plans.plan,
                lazy: (() => import('../pages/operations/plans/plan')) as unknown as LazyRouteFunction<RouteObject>,
              },
            ],
          },
          {
            path: routes.operations.staff.root,
            children: [
              {
                path: routes.operations.staff.list,
                lazy: (() => import('../pages/operations/staff/list')) as unknown as LazyRouteFunction<RouteObject>,
              },
              {
                path: routes.operations.staff.create,
                lazy: (() => import('../pages/operations/staff/create')) as unknown as LazyRouteFunction<RouteObject>,
              },
              {
                path: routes.operations.staff.staff,
                lazy: (() => import('../pages/operations/staff/create')) as unknown as LazyRouteFunction<RouteObject>,
              },
            ],
          },
          {
            path: routes.operations.sessions.root,
            children: [
              {
                path: routes.operations.sessions.list,
                lazy: (() =>
                  import('../pages/operations/session-access/')) as unknown as LazyRouteFunction<RouteObject>,
              },
            ],
          },
        ],
      },
      {
        path: routes.settings.root,
        children: [
          {
            path: routes.settings.business.root,
            lazy: (() => import('../pages/settings/business')) as unknown as LazyRouteFunction<RouteObject>,
            children: [
              {
                path: routes.settings.business.main,
                lazy: (() =>
                  import('../pages/settings/business/locations/list')) as unknown as LazyRouteFunction<RouteObject>,
              },
              {
                path: routes.settings.business.sessionSchedule.root,
                lazy: (() =>
                  import(
                    '../pages/settings/business/sessionSchedule/sessionSchedule'
                  )) as unknown as LazyRouteFunction<RouteObject>,
              },
              {
                path: routes.settings.business.locations.root,
                children: [
                  {
                    path: routes.settings.business.locations.list,
                    lazy: (() =>
                      import('../pages/settings/business/locations/list')) as unknown as LazyRouteFunction<RouteObject>,
                  },
                  {
                    path: routes.settings.business.locations.create,
                    lazy: (() =>
                      import(
                        '../pages/settings/business/locations/create'
                      )) as unknown as LazyRouteFunction<RouteObject>,
                  },
                  {
                    path: routes.settings.business.locations.location,
                    lazy: (() =>
                      import(
                        '../pages/settings/business/locations/location'
                      )) as unknown as LazyRouteFunction<RouteObject>,
                  },
                ],
              },
              {
                path: routes.settings.business.rooms.root,
                children: [
                  {
                    path: routes.settings.business.rooms.list,
                    lazy: (() =>
                      import('../pages/settings/business/rooms/list')) as unknown as LazyRouteFunction<RouteObject>,
                  },
                  {
                    path: routes.settings.business.rooms.create,
                    lazy: (() =>
                      import('../pages/settings/business/rooms/create')) as unknown as LazyRouteFunction<RouteObject>,
                  },
                  {
                    path: routes.settings.business.rooms.room,
                    lazy: (() =>
                      import('../pages/settings/business/rooms/room')) as unknown as LazyRouteFunction<RouteObject>,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: routes.schedule.root,
        children: [
          {
            path: routes.schedule.schedule,
            lazy: (() => import('../pages/schedule/schedule')) as unknown as LazyRouteFunction<RouteObject>,
          },
        ],
      },
      {
        path: routes.myprofile.root,
        lazy: (() => import('../pages/operations/staff/myprofile')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.reports.root,

        children: [
          {
            path: routes.reports.newContacts.root,
            lazy: (() => import('../pages/reports/newContacts')) as unknown as LazyRouteFunction<RouteObject>,
          },
          {
            path: routes.reports.netRevenueReported.root,
            lazy: (() => import('../pages/reports/netRevenueReported')) as unknown as LazyRouteFunction<RouteObject>,
          },
          {
            path: routes.reports.atRiskAttendance.root,
            lazy: (() => import('../pages/reports/atRiskAttendance')) as unknown as LazyRouteFunction<RouteObject>,
          },
          {
            path: routes.reports.basicChurn.root,
            lazy: (() => import('../pages/reports/basicChurn')) as unknown as LazyRouteFunction<RouteObject>,
          },
          {
            path: routes.reports.newSales.root,
            lazy: (() => import('../pages/reports/newSales')) as unknown as LazyRouteFunction<RouteObject>,
          },
          {
            path: routes.reports.challengeConversionDetail.root,
            lazy: (() =>
              import('../pages/reports/challengeConversionDetail')) as unknown as LazyRouteFunction<RouteObject>,
          },
          {
            path: routes.reports.quicksight.root,
            lazy: (() => import('../pages/reports/qsReport.tsx')) as unknown as LazyRouteFunction<RouteObject>,
          },
        ],
      },
    ],
  },
  {
    path: '/',
    Component: AuthLayout as unknown as ComponentType<{}>,
    HydrateFallback: LoadingElement as unknown as ComponentType<{}>,
    errorElement: <RouterFallbackElement />,
    children: [
      {
        path: routes.auth.login,
        lazy: (() => import('../pages/auth/login')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.auth.signup,
        lazy: (() => import('../pages/auth/signup')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.auth.forgotPassword,
        lazy: (() => import('../pages/auth/forgot-password')) as unknown as LazyRouteFunction<RouteObject>,
      },
    ],
  },
  {
    path: '/',
    Component: StaffInvitationLayout as unknown as ComponentType<{}>,
    HydrateFallback: LoadingElement as unknown as ComponentType<{}>,
    errorElement: <RouterFallbackElement />,
    children: [
      {
        path: routes.auth.staffInvitation,
        lazy: (() => import('../pages/auth/staff-invitation')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.auth.forgotPasswordReset,
        lazy: (() => import('../pages/auth/forgot-password-reset')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.auth.forgotPasswordSubmission,
        lazy: (() => import('../pages/auth/forgot-password-submission')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.auth.forgotPasswordUpdate,
        lazy: (() => import('../pages/auth/forgot-password-update')) as unknown as LazyRouteFunction<RouteObject>,
      },
    ],
  },
  {
    path: '/',
    Component: VerifyLayout as unknown as ComponentType<{}>,
    HydrateFallback: LoadingElement as unknown as ComponentType<{}>,
    errorElement: <RouterFallbackElement />,
    children: [
      {
        path: routes.verification.verify,
        lazy: (() => import('../pages/verification/verify')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.verification.verifySuccess,
        lazy: (() => import('../pages/verification/verify-success')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.verification.staffVerify,
        lazy: (() => import('../pages/verification/staff-verify')) as unknown as LazyRouteFunction<RouteObject>,
      },
      {
        path: routes.verification.verificationRequired,
        lazy: (() =>
          import('../pages/verification/verification-required')) as unknown as LazyRouteFunction<RouteObject>,
      },
    ],
  },
  {
    path: '/',
    Component: KioskLayout as unknown as ComponentType<{}>,
    HydrateFallback: LoadingElement as unknown as ComponentType<{}>,
    errorElement: <RouterFallbackElement />,
    children: [
      {
        path: routes.kiosk.checkin,
        lazy: (() => import('../pages/kiosk/checkin')) as unknown as LazyRouteFunction<RouteObject>,
      },
    ],
  },
]);

export default router;
